import CustomDialog from 'common/components/CustomDialog'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { useMutateCreateFarm, useMutateEditFarm } from 'modules/farm/api/mutation'
import { selectedFarmState } from 'modules/farm/stores/atom'
import React, { useEffect, useState, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'

import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import { useFarmGroup } from 'modules/member/api/selector'
import { FarmBody } from 'modules/farm/api/types'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { googleMapsConfig } from 'googleMapsConfig'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import UnsavedChangesDialog from 'common/components/UnsavedChangesDialog'
import { Box, Typography } from '@mui/material'

interface FarmDialogProps {
  open: boolean
  onClose: () => void
  isEditMode?: boolean
  isNameDisabled?: boolean
}

const defaultCenter = {
  lat:
    12.1711922, lng:
    99.8124935
}

const FarmDialog = ({ open, onClose, isEditMode = false, isNameDisabled = false }: FarmDialogProps) => {
  const { t } = useTranslation(['common', 'farm'])
  const [isLoading, setIsLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([])
  const [placesService, setPlacesService] = useState<google.maps.places.AutocompleteService | null>(null)
  const { refetch: refetchFarmGroup } = useFarmGroup('', { enabled: false })
  const searchInputRef = useRef<HTMLInputElement>(null)

  const createFarmMutation = useMutateCreateFarm()
  const editFarmMutation = useMutateEditFarm()

  const farmInfo = useRecoilValue(selectedFarmState)

  const schema = Yup.object().shape({
    name: Yup.string().required(t('farm:please.input.farm.name')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors, isDirty },
  } = useForm<FarmBody>({
    defaultValues: {
      name: '',
      full_address: '',
      lat: defaultCenter.lat,
      lng: defaultCenter.lng,
      province: '',
      district: '',
      subdistrict: '',
      postal_code: '',
    },
    resolver,
  })

  const lat = watch('lat')
  const lng = watch('lng')

  const { isLoaded } = useJsApiLoader(googleMapsConfig);

  useEffect(() => {
    if (isLoaded && (window as any).google && !placesService) {
      setPlacesService(new (window as any).google.maps.places.AutocompleteService())
    }
  }, [isLoaded, placesService])

  const parseAddressComponents = (components?: google.maps.GeocoderAddressComponent[]) => {
    const parsed = {
      full_address: '',
      province: '',
      district: '',
      subdistrict: '',
      postal_code: '',
    }
    if (!components) return parsed
    components.forEach(c => {
      const types = c.types || []
      if (types.includes('postal_code')) parsed.postal_code = c.long_name
      if (types.includes('administrative_area_level_1')) parsed.province = c.long_name
      if (types.includes('administrative_area_level_2')) parsed.district = c.long_name
      if (types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('locality') || types.includes('administrative_area_level_3')) {
        parsed.subdistrict = c.long_name
      }
    })
    return parsed
  }

  const reverseGeocodeAndSet = (latitude: number, longitude: number) => {
    if (!(window as any).google) return
    const geocoder = new (window as any).google.maps.Geocoder()
    geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results: google.maps.GeocoderResult[] | null, status: string) => {
      if (status === 'OK' && results && results[0]) {
        const place = results[0]
        const parsed = parseAddressComponents(place.address_components)
        setValue('full_address', place.formatted_address ?? '', { shouldDirty: true })
        setValue('province', parsed.province ?? '', { shouldDirty: true })
        setValue('district', parsed.district ?? '', { shouldDirty: true })
        setValue('subdistrict', parsed.subdistrict ?? '', { shouldDirty: true })
        setValue('postal_code', parsed.postal_code ?? '', { shouldDirty: true })
        setSearchText(place.formatted_address ?? '')
      }
    })
  }

  useEffect(() => {
    if (isLoaded && lat != null && lng != null) {
      reverseGeocodeAndSet(lat, lng)
    }
  }, [lat, lng, isLoaded])

  useEffect(() => {
    if (isEditMode && farmInfo) {
      reset({
        name: farmInfo.name,
        full_address: '',
        lat: farmInfo.lat ?? defaultCenter.lat,
        lng: farmInfo.lng ?? defaultCenter.lng,
        province: '',
        district: '',
        subdistrict: '',
        postal_code: '',
      })
      setSearchText('')
      if (isLoaded && (farmInfo.lat != null || farmInfo.lng != null)) {
        reverseGeocodeAndSet(farmInfo.lat ?? defaultCenter.lat, farmInfo.lng ?? defaultCenter.lng)
      }
    } else if (!isEditMode) {
      reset({
        name: '',
        full_address: '',
        lat: defaultCenter.lat,
        lng: defaultCenter.lng,
        province: '',
        district: '',
        subdistrict: '',
        postal_code: '',
      })
      setSearchText('')
    }
  }, [isEditMode, farmInfo, reset, isLoaded])

  const handleDialogClose = () => {
    if (isDirty) {
      setConfirmOpen(true)
    } else {
      onClose()
    }
  }

  const handleConfirmDiscard = () => {
    setConfirmOpen(false)
    onClose()
    reset()
    setSearchText('')
  }

  const onSubmit = async (data: FarmBody) => {
    const body: FarmBody = {
      name: data.name,
      full_address: data?.full_address ?? '',
      lat: data.lat,
      lng: data.lng,
      province: data?.province ?? '',
      district: data?.district ?? '',
      subdistrict: data?.subdistrict ?? '',
      postal_code: data?.postal_code ?? '',
    }

    if (isEditMode && farmInfo) {
      await editFarmMutation
        .mutateAsync({ body, farmId: farmInfo.id })
        .then(() => {
          setIsLoading(false)
          onClose()
          reset()
          setSearchText('')
        })
        .catch((error) => {
          const rawMessage = error?.response?.data?.message || ''
          const code = rawMessage.split(':').pop()?.trim()
          if (code === 'FARM_NAME_ALREADY_EXIST') {
            setError('name', {
              type: 'server',
              message: t('farm:duplicate.farm.name'),
            })
          }
          setIsLoading(false)
        })
    } else {
      await createFarmMutation.mutateAsync(body)
        .then(() => {
          setIsLoading(false)
          onClose()
          reset()
          refetchFarmGroup()
          setSearchText('')
        })
        .catch((error) => {
          const rawMessage = error?.response?.data?.message || ''
          const code = rawMessage.split(':').pop()?.trim()
          if (code === 'FARM_NAME_ALREADY_EXIST') {
            setError('name', {
              type: 'server',
              message: t('farm:duplicate.farm.name'),
            })
          }
          setIsLoading(false)
        })
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchText(value)

    if (value.trim() === '') {
      setValue('lat', 0, { shouldDirty: true })
      setValue('lng', 0, { shouldDirty: true })
      setValue('full_address', '', { shouldDirty: true })
      setValue('province', '', { shouldDirty: true })
      setValue('district', '', { shouldDirty: true })
      setValue('subdistrict', '', { shouldDirty: true })
      setValue('postal_code', '', { shouldDirty: true })
      setSuggestions([])
      return
    }

    if (placesService && value) {
      setSearchLoading(true)
      placesService.getPlacePredictions(
        {
          input: value,
        },
        (predictions) => {
          setSuggestions(predictions || [])
          setSearchLoading(false)
        }
      )
    } else {
      setSuggestions([])
    }
  }

  const handleSuggestionClick = (suggestion: google.maps.places.AutocompletePrediction) => {
    if (!(window as any).google) return;

    const map = new (window as any).google.maps.Map(document.createElement('div')); // สร้าง map เปล่า ๆ
    const service = new (window as any).google.maps.places.PlacesService(map);

    service.getDetails(
      {
        placeId: suggestion.place_id,
        language: 'th',
      },
      (place: google.maps.places.PlaceResult | null, status: string) => {
        if (status === 'OK' && place) {
          const lat = place.geometry?.location?.lat() || 0;
          const lng = place.geometry?.location?.lng() || 0;
          const parsed = parseAddressComponents(place.address_components);

          setValue('lat', lat, { shouldDirty: true });
          setValue('lng', lng, { shouldDirty: true });
          setValue('full_address', place.formatted_address ?? '', { shouldDirty: true });
          setValue('province', parsed.province ?? '', { shouldDirty: true });
          setValue('district', parsed.district ?? '', { shouldDirty: true });
          setValue('subdistrict', parsed.subdistrict ?? '', { shouldDirty: true });
          setValue('postal_code', parsed.postal_code ?? '', { shouldDirty: true });
          setSearchText(place.formatted_address ?? suggestion.description);
          setSuggestions([]);
        }
      }
    );
  };


  const title = isEditMode ? t('farm:edit.farm.title') : t('farm:create.farm.title')
  const submitLabel = isEditMode ? t('common:save') : t('common:create')

  return (
    <>
      <CustomDialog
        closeDialog={handleDialogClose}
        open={open}
        title={title}
        submitLabel={submitLabel}
        formName="farm-dialog"
        titleAlign="left"
        isSubmitLoading={isLoading}
        disabledSubmit={isLoading || !isDirty}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)} id="farm-dialog">
          {/* ชื่อฟาร์ม */}
          <Box style={{ marginBottom: 16 }}>
            <Typography style={{ fontWeight: 600 }}>
              {t('farm:farm.title')}
              <Typography component={'span'} style={{ color: 'red', marginLeft: 4 }}>*</Typography>
            </Typography>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  disabled={isEditMode && isNameDisabled}
                  {...field}
                  fullWidth
                  type="text"
                  placeholder={t('farm:please.enter.farm.name')}
                  error={!!errors.name}
                  helperText={errors.name ? errors.name.message : undefined}
                  size="small"
                  sx={{ marginTop: 1 }}
                />
              )}
            />
          </Box>
          {/* ค้นหาแผนที่ (Google Places Autocomplete) */}
          <Box style={{ marginBottom: 16, position: 'relative' }}>
            {isLoaded && (
              <>
                <Typography style={{ fontWeight: 600 }}>
                  {t('farm:please.search.place')}
                </Typography>
                <TextField
                  fullWidth
                  placeholder={t('farm:please.search.place')}
                  size="small"
                  value={searchText}
                  onChange={handleSearchChange}
                  inputRef={searchInputRef}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  autoComplete="off"
                />
                {suggestions.length > 0 && (
                  <List
                    sx={{
                      position: 'absolute',
                      zIndex: 10,
                      width: '100%',
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      maxHeight: 200,
                      overflowY: 'auto',
                    }}
                  >
                    {suggestions.map((s) => (
                      <ListItem key={s.place_id} disablePadding>
                        <ListItemButton onClick={() => handleSuggestionClick(s)}>
                          <ListItemText primary={s.description} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
          {/* แผนที่ (FarmMap) */}
          <Box
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: 250,
            }}
          >
            <Controller
              name="lat"
              control={control}
              render={({ field: { value: lat, onChange: onLatChange } }) => (
                <Controller
                  name="lng"
                  control={control}
                  render={({ field: { value: lng, onChange: onLngChange } }) => (
                    <>
                      {lat && lng && isLoaded ? (
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={{ lat, lng }}
                          zoom={16}
                          options={{
                            disableDefaultUI: true,
                            draggable: true,
                            scrollwheel: true,
                            disableDoubleClickZoom: true,
                            gestureHandling: 'auto',
                          }}
                          onClick={(e) => {
                            const newLat = e.latLng?.lat() || 0
                            const newLng = e.latLng?.lng() || 0
                            onLatChange(newLat)
                            onLngChange(newLng)
                            setValue('lat', newLat, { shouldDirty: true })
                            setValue('lng', newLng, { shouldDirty: true })
                            reverseGeocodeAndSet(newLat, newLng)
                          }}
                        >
                          <Marker
                            position={{ lat, lng }}
                            draggable
                            onDragEnd={(e) => {
                              const newLat = e.latLng?.lat() || 0
                              const newLng = e.latLng?.lng() || 0
                              onLatChange(newLat)
                              onLngChange(newLng)
                              setValue('lat', newLat, { shouldDirty: true })
                              setValue('lng', newLng, { shouldDirty: true })
                              reverseGeocodeAndSet(newLat, newLng)
                            }}
                          />
                        </GoogleMap>
                      ) : (
                        <img
                          src="/static/components/map_placeholder.png"
                          alt="non address"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      )}
                    </>
                  )}
                />
              )}
            />
          </Box>
        </form>
      </CustomDialog>
      <UnsavedChangesDialog
        open={confirmOpen}
        onDiscard={handleConfirmDiscard}
        onCancel={() => setConfirmOpen(false)}
      />
    </>

  )
}
export default FarmDialog
